import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Flex,
  Group,
  Image,
  Stack,
  Text,
} from "@mantine/core";
import {
  Dropzone,
  IMAGE_MIME_TYPE,
  PDF_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  MS_EXCEL_MIME_TYPE,
} from "@mantine/dropzone";
import { useEffect, useRef, useState } from "react";
import { BiDownload, BiTrash } from "react-icons/bi";
import variables from "styles/variables.module.scss";
import { LocalStorageKey } from "@/services/constants";
import { IconAlertCircle, IconX } from "@tabler/icons-react";
import { purfifyHTML } from "@/services/services";
import parse from "html-react-parser";
import { isNil } from "lodash";
import useTrans from "@/hooks/useTrans";
import { generateToken } from "services/otplibServices";

/**
 * Format bytes as human-readable text.
 * 
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use 
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 * 
 * @return Formatted string.
 */
function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


  return bytes.toFixed(dp) + ' ' + units[u];
}

/**
 * Renders a dynamic input dropzone based on the input template type.
 * @param {object} props - The props containing inputTemplate type.
 * @returns {JSX.Element} - The rendered input dropzone.
 */
function FormDynamicInputDropzone(props) {
  // Initialize eleHTML as a fragment
  let eleHTML = <></>;

  // Render different dropzone components based on inputTemplate type
  switch (props.inputTemplate.type) {
    case "drop-zone":
      eleHTML = <ImageDropzone {...props} />;
      break;

    case "file-picker":
      eleHTML = <FileDropzone {...props} />;
      break;

    case "file-picker-many":
      eleHTML = <FileManyDropzone {...props} />;
      break;
    case "generic-file-picker":
      eleHTML = <GenericDropzone {...props} />;
      break;
    default:
      break;
  }

  return eleHTML;
}

function ImageDropzone(props) {
  const zoneWidth = 180;
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [imageFile, setImageFile] = useState(null);
  const [oldInvisible, setOldInvisible] = useState(false);

  const trans = useTrans();
  const inputProps = { ...props.formInputProps };
  const inputTemplate = { ...props.inputTemplate };
  const disabled = props.disabledInput || inputProps.disabled;

  const actualHostName =
    process.env.NEXT_PUBLIC_API_URL || `https://${props.hostNameUrl}/api`;

  function getFilename(pathOrUrl) {
    const match = pathOrUrl.match(
      /^https?:\/\/[^\s?#]+\.(png|jpg|jpeg|gif|webp|svg)(?:[?#].*)?$/i,
    );
    return match ? match[1] : "";
  }

  function getImageMime(filename) {
    if (!filename) return "application/octet-stream";
    const ext = filename?.split(".").pop()?.toLowerCase();
    const map = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
      bmp: "image/bmp",
      tiff: "image/tiff",
    };
    return ext && map[ext] ? map[ext] : "application/octet-stream";
  }

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length < 0) return;
    setError(null);

    const file = files[0];
    if (!file.type?.startsWith("image/")) return;

    const url = URL.createObjectURL(file);
    const name = file.name;
    const type = file.type;

    setImageFile({ name, type, url });
    props.formInputProps.onChange(file);
    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
          (e) => e.inputName === props.inputName,
        )
        : null;
    if (index !== null) {
      let copyFileToUploadArray;
      if (index >= 0) {
        copyFileToUploadArray = [
          ...props.fileToUploadArray.slice(0, index),
          ...props.fileToUploadArray.slice(index + 1),
        ];
      } else {
        copyFileToUploadArray = [...props.fileToUploadArray];
      }
      copyFileToUploadArray.push({
        inputName: props.inputName,
        file: file,
        inputTemplate: props.inputTemplate,
      });
      props.setFileToUploadArray(copyFileToUploadArray);
    }
  };

  useEffect(() => {
    if (isNil(props.inputTemplate?.maxSize) || typeof props.inputTemplate?.maxSize != "number") return
    setMaxSize(props.inputTemplate?.maxSize)
  }, [props.inputTemplate?.maxSize])

  useEffect(() => {
    if (typeof props.initialValue === "string") {
      if (props.initialValue === "") {
        setImageFile(null);
      } else {
        const filename = getFilename(props.initialValue);
        if (filename !== "") {
          let objTemp = {
            name: filename,
            type: getImageMime(filename),
            url: `${props.initialValue}`,
          };
          setImageFile(objTemp);
          props.formInputProps.onChange(objTemp);
        } else {
          setImageFile(null);
          props.formInputProps.onChange(null);
        }
        return;
      }
      return;
    }
    setImageFile(props.initialValue);
  }, [props.initialValue]);

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      props.formInputProps.onChange(undefined);
    } else if (oldInvisible) {
      setOldInvisible(false);
      props.formInputProps.onChange(
        props.initialValue === undefined ? null : props.initialValue,
      );
    }
  }, [props.inputTemplate.invisible]);

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;

    switch (firstError) {
      case "file-too-large":
        setError(`${props.inputTemplate?.errorSizeMsg || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`);
        break;

      case "file-invalid-type":
        setError(`${props.inputTemplate?.errorTypeMsg || trans.formDynamic.file.errorType}`);
        break;

      case "too-many-files":
        setError(`${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorType}`);
        break;

      default:
        setError(`${props.inputTemplate?.errorGenericMsg || trans.formDynamic.file.errorGeneric}`);
    }
  };

  const uploadImageJSX = (
    <Stack spacing={25} pt={25} pl={25} pr={25} pb={34} mt={15}>
      <Center>
        <Image width={32} alt={""} src={"/images/cloud-upload.svg"} />
      </Center>
      <Text ta="center" fz={16} c={"rgba(21, 20, 57, 0.8)"}>
        Kéo thả hình ảnh ở đây{" "}
        {props.requiredInput && <span style={{ color: "red" }}>{" *"}</span>}
      </Text>
    </Stack>
  );
  const previewImageJSX = () => {
    if (!imageFile) return <></>;
    return (
      <Image
        pl={0}
        pt={8}
        radius={10}
        width={zoneWidth - 20}
        height={zoneWidth - 20}
        src={
          imageFile.url !== ""
            ? imageFile.url
            : actualHostName + "/portals/file-manager/" + imageFile.name + "?dynamic_token=" + generateToken()
        }
        alt={imageFile.name}
      />
    );
  };

  const closeIconJSX = (
    <ActionIcon
      color="red"
      radius="xl"
      size="lg"
      variant="filled"
      style={{
        position: "absolute",
        zIndex: 10,
        top: `-10px`,
        right: `calc( 0.5 * (100% - ${zoneWidth}px) - 10px )`,
      }}
      onClick={() => {
        setImageFile(null);
        props.formInputProps.onChange("");
        props.setFileToUploadArray([]);
      }}
    >
      <IconX size={18} />
    </ActionIcon>
  );

  return (
    <Center style={{ marginBottom: 20, position: "relative" }}>
      {/* {((imageFile && !props.disabledInput) || inputProps.disabled) &&
        closeIconJSX} */}
      <Stack>
        <Center>
          <Dropzone
            accept={IMAGE_MIME_TYPE}
            disabled={disabled}
            multiple={false}
            maxSize={maxSize}
            {...inputProps}
            style={{
              width: zoneWidth,
              height: zoneWidth,
              padding: 0,
              borderRadius: 8,
              backgroundColor: disabled && "transparent",
              cursor: disabled && "not-allowed",
              border: !isNil(inputProps.error) && "2px dashed red",
            }}
          >
            <Center>{imageFile ? previewImageJSX() : uploadImageJSX}</Center>
          </Dropzone>
        </Center>
        {inputProps.error ? (
          <div
            style={{
              marginTop: -5,
              marginBottom: 20,
              color: "red",
              fontSize: 12,
            }}
          >
            {inputProps.error}
          </div>
        ) : (
          <></>
        )}
        {error && (
          <div
            style={{
              marginBottom: 10,
              color: "red",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}
        {inputTemplate?.description && (
          <>{parse(purfifyHTML(inputTemplate?.description))}</>
        )}
      </Stack>
    </Center>
  );
}

function FileDropzone(props) {
  const openRef = useRef(null);
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [filePreview, setFilePreview] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [oldInvisible, setOldInvisible] = useState(false);

  const trans = useTrans();
  const inputProps = { ...props.formInputProps };
  const disabled = props.disabledInput || inputProps.disabled;
  const actualHostName =
    process.env.NEXT_PUBLIC_API_URL || `https://${props.hostNameUrl}/api`;

  let acceptedFileType = [...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE];
  switch (localStorage.getItem(LocalStorageKey.TENANT)) {
    case "svfc":
      acceptedFileType = [
        ...acceptedFileType,
        ...MS_WORD_MIME_TYPE,
        ...MS_EXCEL_MIME_TYPE,
      ];
    case "shb":
      acceptedFileType = [
        ...PDF_MIME_TYPE,
        ...MS_WORD_MIME_TYPE,
        ...MS_EXCEL_MIME_TYPE,
      ];
      break;
  }

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length < 0) return;

    setError(null);

    const file = files[0];
    const url = URL.createObjectURL(file);
    const name = file.name;
    const type = file.type;

    setHasUploaded(true);
    setFilePreview({ name, type, url });
    props.formInputProps.onChange(file);

    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
          (e) => e.inputName === props.inputName,
        )
        : null;
    if (index !== null) {
      let copyFileToUploadArray;
      if (index >= 0) {
        copyFileToUploadArray = [
          ...props.fileToUploadArray.slice(0, index),
          ...props.fileToUploadArray.slice(index + 1),
        ];
      } else {
        copyFileToUploadArray = [...props.fileToUploadArray];
      }
      copyFileToUploadArray.push({
        inputName: props.inputName,
        file: file,
        inputTemplate: props.inputTemplate,
      });
      props.setFileToUploadArray(copyFileToUploadArray);
    }
  };

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;

    switch (firstError) {
      case "file-too-large":
        setError(`${props.inputTemplate?.errorSizeMsg || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`);
        break;

      case "file-invalid-type":
        setError(`${props.inputTemplate?.errorTypeMsg || trans.formDynamic.file.errorType}`);
        break;

      case "too-many-files":
        setError(`${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorType}`);
        break;

      default:
        setError(`${props.inputTemplate?.errorGenericMsg || trans.formDynamic.file.errorGeneric}`);
    }
  };

  useEffect(() => {
    if (isNil(props.inputTemplate?.maxSize) || typeof props.inputTemplate?.maxSize != "number") return
    setMaxSize(props.inputTemplate?.maxSize)
  }, [props.inputTemplate?.maxSize])

  useEffect(() => {
    if (hasUploaded) {
      setHasUploaded(false);
    }
  }, [props.isFormSubmitted]);

  useEffect(() => {
    setFilePreview(props.initialValue);
  }, [props.initialValue]);

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      handleDelete(true);
    } else if (oldInvisible) {
      setOldInvisible(false);
      setFilePreview(null);
      inputProps.onChange(
        props.initialValue === undefined ? null : props.initialValue,
      );
    }
  }, [props.inputTemplate.invisible]);

  const handleDelete = (invisible = false) => {
    setFilePreview(invisible ? undefined : null);
    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
          (e) => e.inputName === props.inputName,
        )
        : null;

    if (index !== null && index >= 0) {
      const copyFileToUploadArray = [
        ...props.fileToUploadArray.slice(0, index),
        ...props.fileToUploadArray.slice(index + 1),
      ];
      props.setFileToUploadArray(copyFileToUploadArray);
    }
    props.formInputProps.onChange(invisible ? undefined : null);
  };

  const filePreviewJSX = filePreview ? (
    <Center>
      <Text
        style={{
          width: "70%",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
        ta={"center"}
        fz={16}
      >
        {filePreview.name && filePreview.name !== ""
          ? filePreview.name
          : "Failed to fetch filename"}
      </Text>
      <Flex gap={10} align={"center"}>
        <ActionIcon
          color="dark"
          style={{
            cursor: hasUploaded && "not-allowed",
            color: hasUploaded && "lightgray",
            backgroundColor: hasUploaded && "transparent",
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (hasUploaded) return;
            window.open(
              filePreview.url !== ""
                ? filePreview.url
                : actualHostName + "/portals/file-manager/" + filePreview.name + "?dynamic_token=" + generateToken(),
              "_blank",
            );
          }}
        >
          <BiDownload size={"1.3rem"} />
        </ActionIcon>
        {!disabled && (
          <ActionIcon
            color="red"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleDelete();
            }}
          >
            <BiTrash size={"1.3rem"} />
          </ActionIcon>
        )}
      </Flex>
    </Center>
  ) : (
    // <Center>
    //   <Image width={32} alt={""} src={"/images/cloud-disabled.svg"} />
    // </Center>
    <></>
  );

  return (
    <Dropzone
      openRef={openRef}
      accept={acceptedFileType}
      {...inputProps}
      disabled={disabled}
      multiple={false}
      maxSize={maxSize}
      style={{
        marginTop: 20,
        backgroundColor: disabled && "transparent",
        cursor: disabled && "not-allowed",
      }}
      onReject={handleReject}
      styles={{ inner: { pointerEvents: "all" } }}
    >
      <Dropzone.Idle>
        <Stack spacing={10} w={"100%"}>
          {props.inputTemplate.label ? (
            <Text fw={500} c={variables.primaryColor} lineClamp={2}>
              {props.inputTemplate.label}
              {props.requiredInput && (
                <span style={{ color: "red" }}>{" *"}</span>
              )}
            </Text>
          ) : (
            <></>
          )}

          {inputProps.error ? (
            <div
              style={{
                marginTop: -5,
                marginBottom: 10,
                color: "red",
                fontSize: 12,
              }}
            >
              {inputProps.error}
            </div>
          ) : (
            <></>
          )}
          {error && (
            <div
              style={{
                marginBottom: 10,
                color: "red",
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {filePreviewJSX}
          {!props.disabledInput && (
            <Group position="center">
              <Button
                onClick={() => {
                  openRef.current();
                }}
              >
                {props.inputTemplate.buttonLabel ||
                  "Tải hình ảnh/tải danh sách"}
              </Button>
            </Group>
          )}
        </Stack>
      </Dropzone.Idle>
    </Dropzone>
  );
}

function FileManyDropzone(props) {
  const openRef = useRef(null);
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [filePreview, setFilePreview] = useState([]);
  const [hasUploadedIdx, setHasUploadedIdx] = useState([]);
  const [oldInvisible, setOldInvisible] = useState(false);

  const trans = useTrans();
  const inputProps = { ...props.formInputProps };
  const disabled = props.disabledInput;

  let acceptedFileType = [...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE];
  switch (localStorage.getItem(LocalStorageKey.TENANT)) {
    case "svfc":
      acceptedFileType = [
        ...acceptedFileType,
        ...MS_WORD_MIME_TYPE,
        ...MS_EXCEL_MIME_TYPE,
      ];
      break;
    case "shb":
      acceptedFileType = [
        ...PDF_MIME_TYPE,
        ...MS_WORD_MIME_TYPE,
        ...MS_EXCEL_MIME_TYPE,
      ];
      break;
  }

  async function fetchFileObject({ url, name, type }) {
    const res = await fetch(url + "?dynamic_token=" + generateToken());
    const blob = await res.blob();
    return new File([blob], name, { type: blob.type || type });
  }

  async function convertFiles(data) {
    if (!Array.isArray(data)) {
      return [];
    }
    return Promise.all(
      data.map((item) => {
        if (item.url && item.name) {
          return fetchFileObject(item);
        }
        return item;
      }),
    );
  }

  const handleReject = (rejections) => {
    console.log("please trigger", rejections)
    const firstError = rejections[0]?.errors[0]?.code;

    switch (firstError) {
      case "file-too-large":
        setError(`${props.inputTemplate?.errorSizeMsg || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`);
        break;

      case "file-invalid-type":
        setError(`${props.inputTemplate?.errorTypeMsg || trans.formDynamic.file.errorType}`);
        break;

      case "too-many-files":
        setError(`${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorType}`);
        break;

      default:
        setError(`${props.inputTemplate?.errorGenericMsg || trans.formDynamic.file.errorGeneric}`);
    }
  };

  useEffect(() => {
    console.log("error", error)
  }, [error])

  useEffect(() => {
    if (isNil(props.inputTemplate?.maxSize) || typeof props.inputTemplate?.maxSize != "number") return
    setMaxSize(props.inputTemplate?.maxSize)
  }, [props.inputTemplate?.maxSize])

  useEffect(() => {
    let tempFileArr = props.initialValue?.filter(
      (e) => e !== null && Object.keys(e).length !== 0,
    );

    convertFiles(tempFileArr).then((result) => {
      setFilePreview(result ?? []);
    });
  }, [props.initialValue]);

  inputProps.onDrop = (files) => {
    const filePreviewCopy = [...filePreview, ...files];

    if (props.fileToUploadArray && props.setFileToUploadArray) {
      const copyFileToUploadArray = [...props.fileToUploadArray];

      const masterIndex = props.fileToUploadArray.findIndex(
        (e) => e.inputName === props.inputName,
      );
      if (masterIndex >= 0) {
        copyFileToUploadArray[masterIndex].file.push(...files);
      } else {
        copyFileToUploadArray.push({
          inputName: props.inputName,
          file: filePreviewCopy,
          inputTemplate: props.inputTemplate,
        });
      }

      props.setFileToUploadArray(copyFileToUploadArray);
    }
    props.formInputProps.onChange(filePreviewCopy);
    setFilePreview(filePreviewCopy);
  };

  const handleDeleteFile = (index) => {
    const filePreviewCopy = [
      ...filePreview.slice(0, index),
      ...filePreview.slice(index + 1),
    ];
    props.formInputProps.onChange(filePreviewCopy);
    setFilePreview(filePreviewCopy);

    if (props.fileToUploadArray && props.setFileToUploadArray) {
      const masterIndex = props.fileToUploadArray.findIndex(
        (e) => e.inputName === props.inputName,
      );
      // const copyFileToUploadArray = [...props.fileToUploadArray.slice(0, masterIndex + index), ...props.fileToUploadArray.slice(masterIndex + index + 1)];

      let copyFileToUploadArray = [...props.fileToUploadArray];
      if (masterIndex >= 0) {
        const copyOfFiles = [...copyFileToUploadArray[masterIndex].file];
        copyFileToUploadArray[masterIndex].file = [
          ...copyOfFiles.slice(0, index),
          ...copyOfFiles.slice(index + 1),
        ];

        if (copyFileToUploadArray[masterIndex].file.length <= 0) {
          copyFileToUploadArray = [
            ...copyFileToUploadArray.slice(0, masterIndex),
            ...copyFileToUploadArray.slice(masterIndex + 1),
          ];
        }

        props.setFileToUploadArray(copyFileToUploadArray);
      }
    }
  };

  const handleDeleteAll = () => {
    props.formInputProps.onChange(undefined);
    setFilePreview([]);
    const masterIndex = props.fileToUploadArray.findIndex(
      (e) => e.inputName === props.inputName,
    );
    let copyFileToUploadArray = [...props.fileToUploadArray];
    copyFileToUploadArray = [
      ...copyFileToUploadArray.slice(0, masterIndex),
      ...copyFileToUploadArray.slice(masterIndex + 1),
    ];
    props.setFileToUploadArray(copyFileToUploadArray);
  };

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      handleDeleteAll();
    } else if (oldInvisible) {
      setOldInvisible(false);
      props.formInputProps.onChange(
        Array.isArray(props.initialValue) ? props.initialValue : [],
      );
    }
  }, [props.inputTemplate.invisible]);

  const filePreviewJSX =
    filePreview && Array.isArray(filePreview) && filePreview.length > 0 ? (
      <Center>
        <Stack spacing={3} style={{ overflow: "hidden" }}>
          {filePreview.map((filePreviewSingle, i) => {
            return (
              <Group
                key={i}
                style={{ width: "100%", justifyContent: "center", overflow: "hidden" }}
                spacing={10}
                align={"center"}
                noWrap
              >
                <Text
                  key={i}
                  lineClamp={1}
                  ta={"center"}
                  fz={16}
                >
                  {filePreviewSingle.name}
                </Text>
                <Flex gap={10} align={"center"}>
                  {/* <ActionIcon
                    color="dark"
                    style={
                      {
                        // cursor: hasUploaded && "not-allowed",
                        // color: hasUploaded && "lightgray",
                        // backgroundColor: hasUploaded && "transparent",
                      }
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      console.log("balls");
                    }}
                  >
                    <BiDownload size={"1.3rem"} />
                  </ActionIcon> */}
                  {!disabled && (
                    <ActionIcon
                      color="red"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleDeleteFile(i);
                      }}
                    >
                      <BiTrash size={"1.3rem"} />
                    </ActionIcon>
                  )}
                </Flex>
              </Group>
            );
          })}
        </Stack>
      </Center>
    ) : (
      // <Center>
      //   <Image width={32} alt={""} src={"/images/cloud-disabled.svg"} />
      // </Center>
      <></>
    );

  return (
    <Dropzone
      openRef={openRef}
      accept={acceptedFileType}
      multiple
      {...inputProps}
      style={{
        marginTop: 20,
        backgroundColor: disabled && "transparent",
        cursor: disabled && "not-allowed",
      }}
      maxSize={maxSize}
      styles={{
        inner: { pointerEvents: "all" },
      }}
      disabled={disabled}
      onReject={handleReject}
    >
      <Dropzone.Idle>
        <Stack spacing={10}>
          {props.inputTemplate.label ? (
            <Text fw={500} c={variables.primaryColor}>
              {props.inputTemplate.label}
              {props.requiredInput && (
                <span style={{ color: "red" }}>{" *"}</span>
              )}
            </Text>
          ) : (
            <></>
          )}
          {!props.disabledInput && (
            <Group position="center">
              <Button
                onClick={() => {
                  openRef.current();
                }}
              >
                Tải hình ảnh/tải danh sách
              </Button>
            </Group>
          )}

          {inputProps.error ? (
            <div
              style={{
                marginTop: -5,
                marginBottom: 20,
                color: "red",
                fontSize: 12,
              }}
            >
              {inputProps.error}
            </div>
          ) : (
            <></>
          )}
          {error && (
            <div
              style={{
                marginBottom: 10,
                color: "red",
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {filePreviewJSX}
        </Stack>
      </Dropzone.Idle>
    </Dropzone >
  );
}

function GenericDropzone(props) {
  const openRef = useRef(null);
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [filePreview, setFilePreview] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [oldInvisible, setOldInvisible] = useState(false);

  const trans = useTrans();
  const inputProps = { ...props.formInputProps };
  const disabled = props.disabledInput || inputProps.disabled;
  const actualHostName =
    process.env.NEXT_PUBLIC_API_URL || `https://${props.hostNameUrl}/api`;

  let acceptedFileType = [...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE];
  switch (localStorage.getItem(LocalStorageKey.TENANT)) {
    case "svfc":
      acceptedFileType = [
        ...acceptedFileType,
        ...MS_WORD_MIME_TYPE,
        ...MS_EXCEL_MIME_TYPE,
      ];
    case "shb":
      acceptedFileType = [
        ...PDF_MIME_TYPE,
        ...MS_WORD_MIME_TYPE,
        ...MS_EXCEL_MIME_TYPE,
      ];
      break;
  }

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length < 0) return;

    setError(null);

    const file = files[0];
    const url = URL.createObjectURL(file);
    const name = file.name;
    const type = file.type;

    setHasUploaded(true);
    setFilePreview({ name, type, url });
    props.formInputProps.onChange(file);

    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
          (e) => e.inputName === props.inputName,
        )
        : null;
    if (index !== null) {
      let copyFileToUploadArray;
      if (index >= 0) {
        copyFileToUploadArray = [
          ...props.fileToUploadArray.slice(0, index),
          ...props.fileToUploadArray.slice(index + 1),
        ];
      } else {
        copyFileToUploadArray = [...props.fileToUploadArray];
      }
      copyFileToUploadArray.push({
        inputName: props.inputName,
        file: file,
        inputTemplate: props.inputTemplate,
      });
      props.setFileToUploadArray(copyFileToUploadArray);
    }
  };

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;

    switch (firstError) {
      case "file-too-large":
        setError(`${props.inputTemplate?.errorSizeMsg || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`);
        break;

      case "file-invalid-type":
        setError(`${props.inputTemplate?.errorTypeMsg || trans.formDynamic.file.errorType}`);
        break;

      case "too-many-files":
        setError(`${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorType}`);
        break;

      default:
        setError(`${props.inputTemplate?.errorGenericMsg || trans.formDynamic.file.errorGeneric}`);
    }
  };

  useEffect(() => {
    if (isNil(props.inputTemplate?.maxSize) || typeof props.inputTemplate?.maxSize != "number") return
    setMaxSize(props.inputTemplate?.maxSize)
  }, [props.inputTemplate?.maxSize])

  useEffect(() => {
    if (hasUploaded) {
      setHasUploaded(false);
    }
  }, [props.isFormSubmitted]);

  useEffect(() => {
    setFilePreview(props.initialValue);
  }, [props.initialValue]);

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      handleDelete(true);
    } else if (oldInvisible) {
      setOldInvisible(false);
      setFilePreview(null);
      inputProps.onChange(
        props.initialValue === undefined ? null : props.initialValue,
      );
    }
  }, [props.inputTemplate.invisible]);

  const handleDelete = (invisible = false) => {
    setFilePreview(invisible ? undefined : null);
    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
          (e) => e.inputName === props.inputName,
        )
        : null;

    if (index !== null && index >= 0) {
      const copyFileToUploadArray = [
        ...props.fileToUploadArray.slice(0, index),
        ...props.fileToUploadArray.slice(index + 1),
      ];
      props.setFileToUploadArray(copyFileToUploadArray);
    }
    props.formInputProps.onChange(invisible ? undefined : null);
  };

  const uploadFileJSX = (
    <Stack spacing={8}>
      <Center>
        <Image width={24} alt={""} src={"/images/cloud-upload.svg"} />
      </Center>
      <Stack spacing={0}>
        {props.inputTemplate.label ? (
          <Text fw={700} lineClamp={2} ta="center">
            {props.inputTemplate.label}
            {props.requiredInput && (
              <span style={{ color: "red" }}>{" *"}</span>
            )}
          </Text>
        ) : (
          <></>
        )}
        <Text ta="center" fz={14} c={"gray"}>
          {props.inputTemplate?.placeholder || "Kéo thả hình ảnh ở đây"}
        </Text>
      </Stack>
    </Stack>
  );

  const filePreviewJSX = filePreview ? (
    <Center>
      <Text
        lineClamp={1}
        ta={"center"}
        fz={16}
      >
        {filePreview.name && filePreview.name !== ""
          ? filePreview.name
          : "Failed to fetch filename"}
      </Text>
      <Flex gap={10} align={"center"}>
        <ActionIcon
          color="dark"
          style={{
            cursor: hasUploaded && "not-allowed",
            color: hasUploaded && "lightgray",
            backgroundColor: hasUploaded && "transparent",
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (hasUploaded) return;
            window.open(
              filePreview.url !== ""
                ? filePreview.url
                : actualHostName + "/portals/file-manager/" + filePreview.name + "?dynamic_token=" + generateToken(),
              "_blank",
            );
          }}
        >
          <BiDownload size={"1.3rem"} />
        </ActionIcon>
        {!disabled && (
          <ActionIcon
            color="red"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleDelete();
            }}
          >
            <BiTrash size={"1.3rem"} />
          </ActionIcon>
        )}
      </Flex>
    </Center>
  ) : (
    // <Center>
    //   <Image width={32} alt={""} src={"/images/cloud-disabled.svg"} />
    // </Center>
    <></>
  );

  return (
    <Dropzone
      openRef={openRef}
      accept={acceptedFileType}
      {...inputProps}
      disabled={disabled}
      multiple={false}
      maxSize={maxSize}
      style={{
        marginTop: 20,
        backgroundColor: disabled && "transparent",
        cursor: disabled && "not-allowed",
        border: "1px dashed lightgray"
      }}
      onReject={handleReject}
      styles={{ inner: { pointerEvents: "all" } }}
    >
      <Dropzone.Idle>
        <Stack spacing={10} w={"100%"} py={10}>
          {inputProps.error ? (
            <div
              style={{
                marginTop: -5,
                marginBottom: 10,
                color: "red",
                fontSize: 12,
              }}
            >
              {inputProps.error}
            </div>
          ) : (
            <></>
          )}
          {error && (
            <div
              style={{
                marginBottom: 10,
                color: "red",
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {isNil(filePreview) ? uploadFileJSX : filePreviewJSX}
        </Stack>
      </Dropzone.Idle>
    </Dropzone>
  );
}

export default FormDynamicInputDropzone;
